import { useCallback, useEffect, useRef, useState } from 'react';
import {
  storageApiRef,
  useApi,
  type StorageApi,
} from '@backstage/core-plugin-api';
import {
  addLink,
  editLink,
  moveLink,
  newQuicklink,
  removeLink,
  setLinkColor,
  setLinkIcon,
  QUICKLINKS_BUCKET,
  QUICKLINKS_KEY,
  type Quicklink,
} from './quicklinks';

// Quicklink is a named interface (no JSON index signature), so cast through
// the StorageApi's own value type at the bucket boundary rather than pulling
// in @backstage/types just for JsonValue.
type StorageValue = Parameters<StorageApi['set']>[1];

export interface UseQuicklinks {
  links: Quicklink[];
  /** Appends a link and returns its generated id (so the caller can edit it). */
  add(v: { title: string; url: string; icon?: string; color?: string }): string;
  remove(id: string): void;
  edit(id: string, patch: { title: string; url: string }): void;
  setIcon(id: string, icon: string): void;
  setColor(id: string, color: string): void;
  move(id: string, dir: 'up' | 'down'): void;
}

/**
 * Per-user quicklinks, persisted in the DB-backed `quicklinks` StorageApi bucket
 * (survives reload, syncs across devices). The observe$ stream is the source of
 * truth; every mutation writes the whole array back and re-renders on the echo.
 */
export function useQuicklinks(): UseQuicklinks {
  const storage = useApi(storageApiRef);
  const bucket = storage.forBucket(QUICKLINKS_BUCKET);
  const [links, setLinks] = useState<Quicklink[]>([]);
  // Keep the latest list for read-modify-write without stale closures.
  const ref = useRef<Quicklink[]>([]);
  ref.current = links;

  useEffect(() => {
    const sub = bucket.observe$(QUICKLINKS_KEY).subscribe(snapshot => {
      if (snapshot.presence === 'present' && Array.isArray(snapshot.value)) {
        setLinks(snapshot.value as unknown as Quicklink[]);
      } else if (snapshot.presence === 'absent') {
        setLinks([]);
      }
    });
    return () => sub.unsubscribe();
    // forBucket returns a fresh wrapper each render; depend on the stable storage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storage]);

  const write = useCallback(
    (next: Quicklink[]) => {
      // Update the ref + state synchronously (optimistic) so rapid successive
      // mutations compose off the latest list: the DB-backed StorageApi's set()
      // echoes through observe$ only after a network round-trip, so reading the
      // echoed list alone would let in-flight writes clobber each other (e.g. a
      // double-clicked "Add", or several quick reorders).
      ref.current = next;
      setLinks(next);
      void bucket.set(QUICKLINKS_KEY, next as unknown as StorageValue);
    },
    [bucket],
  );

  const add = useCallback(
    (v: { title: string; url: string; icon?: string; color?: string }) => {
      const link = newQuicklink(v);
      write(addLink(ref.current, link));
      return link.id;
    },
    [write],
  );
  const remove = useCallback(
    (id: string) => write(removeLink(ref.current, id)),
    [write],
  );
  const edit = useCallback(
    (id: string, patch: { title: string; url: string }) =>
      write(editLink(ref.current, id, patch)),
    [write],
  );
  const setIcon = useCallback(
    (id: string, icon: string) => write(setLinkIcon(ref.current, id, icon)),
    [write],
  );
  const setColor = useCallback(
    (id: string, color: string) =>
      write(setLinkColor(ref.current, id, color)),
    [write],
  );
  const move = useCallback(
    (id: string, dir: 'up' | 'down') => write(moveLink(ref.current, id, dir)),
    [write],
  );

  return { links, add, remove, edit, setIcon, setColor, move };
}
